import { PourReportDto, PourStatus } from 'providers/api';
import { StopeShapeDateRange, toEpochDay } from './stopeShapeDateRange';

// A single pour reduced to the fields the 3D fill timeline needs.
export interface StopePourEvent {
  intendedDay: number | null;
  plannedVolume: number;
  completedDay: number | null;
  completedVolume: number;
}

export interface StopePourTimeline {
  stopeId: string;
  events: StopePourEvent[];
}

export type StopePourData = Record<string, StopePourTimeline>;

export type AnimationStep = 'day' | 'week' | 'month';

export const ANIMATION_STEP_DAYS: Record<AnimationStep, number> = { day: 1, week: 7, month: 30 };

export interface StopeFillState {
  completedRatio: number;
  plannedRatio: number;
  isActive: boolean;
  hasDue: boolean;
}

// Pours in these states never contribute fill (cancelled / rejected work).
const isExcludedStatus = (status: PourStatus | undefined): boolean => (
  status === PourStatus.Abandoned || status === PourStatus.Rejected
);

const clamp01 = (value: number): number => Math.min(1, Math.max(0, value));

const toEvent = (pour: PourReportDto): StopePourEvent => {
  const intendedDay = toEpochDay(pour.intendedPourDate);
  const isCompleted = pour.status === PourStatus.Complete;
  const completedDay = isCompleted ? toEpochDay(pour.pourData?.pourCompletedDate) ?? intendedDay : null;
  const completedVolume = isCompleted ? pour.pourData?.volume ?? pour.volume ?? 0 : 0;

  return {
    intendedDay,
    plannedVolume: pour.volume ?? 0,
    completedDay,
    completedVolume,
  };
};

export const buildStopePourData = (pours: PourReportDto[]): StopePourData => pours
  .filter((pour) => !!pour.stopeId && !isExcludedStatus(pour.status))
  .reduce<StopePourData>((acc, pour) => {
    const timeline = acc[pour.stopeId] ?? { stopeId: pour.stopeId, events: [] };
    timeline.events.push(toEvent(pour));
    acc[pour.stopeId] = timeline;
    return acc;
  }, {});

// Cumulative actual fill (green) on/before the given day.
export const completedVolumeAtDay = (timeline: StopePourTimeline, day: number): number => timeline.events
  .reduce((sum, event) => (event.completedDay !== null && event.completedDay <= day ? sum + event.completedVolume : sum), 0);

// Planned volume that has come due (intended on/before the day) but is not yet poured by that day (orange).
export const pendingPlannedVolumeAtDay = (timeline: StopePourTimeline, day: number): number => timeline.events
  .reduce((sum, event) => {
    const due = event.intendedDay !== null && event.intendedDay <= day;
    const pouredByDay = event.completedDay !== null && event.completedDay <= day;
    return due && !pouredByDay ? sum + event.plannedVolume : sum;
  }, 0);

// True when a pour was intended or completed within [startDay, endDay) — i.e. this stope is being filled in the current step.
export const hasPourInWindow = (timeline: StopePourTimeline, startDay: number, endDay: number): boolean => timeline.events
  .some((event) => (
    (event.completedDay !== null && event.completedDay >= startDay && event.completedDay < endDay)
    || (event.intendedDay !== null && event.intendedDay >= startDay && event.intendedDay < endDay)
  ));

// Fill state when no timeline day is set: use every pour's final contribution.
const totalCompletedVolume = (timeline: StopePourTimeline): number => timeline.events
  .reduce((sum, event) => sum + event.completedVolume, 0);

const totalPlannedCapVolume = (timeline: StopePourTimeline): number => timeline.events
  .reduce((sum, event) => {
    const poured = event.completedDay !== null;
    return sum + (poured ? event.completedVolume : event.plannedVolume);
  }, 0);

export const getStopeFillAtDay = (
  timeline: StopePourTimeline | undefined,
  stopeVolume: number | undefined,
  day: number | null | undefined,
  windowStartDay: number | null,
  windowEndDay: number | null,
): StopeFillState => {
  const empty: StopeFillState = { completedRatio: 0, plannedRatio: 0, isActive: false, hasDue: false };
  if (!timeline || !stopeVolume || stopeVolume <= 0) return empty;

  if (day == null) {
    const completed = totalCompletedVolume(timeline);
    const cap = totalPlannedCapVolume(timeline);
    return {
      completedRatio: clamp01(completed / stopeVolume),
      plannedRatio: clamp01(cap / stopeVolume),
      isActive: false,
      hasDue: cap > 0,
    };
  }

  const completed = completedVolumeAtDay(timeline, day);
  const pending = pendingPlannedVolumeAtDay(timeline, day);
  const completedRatio = clamp01(completed / stopeVolume);
  const plannedRatio = clamp01((completed + pending) / stopeVolume);
  const isActive = windowStartDay !== null && windowEndDay !== null
    && hasPourInWindow(timeline, windowStartDay, windowEndDay);

  return {
    completedRatio,
    plannedRatio,
    isActive,
    hasDue: completed > 0 || pending > 0,
  };
};

// Slider domain from real pour dates (intended + completed), across all stopes.
export const getPourDateDomain = (data: StopePourData): StopeShapeDateRange | null => {
  let minEpochDay = Number.POSITIVE_INFINITY;
  let maxEpochDay = Number.NEGATIVE_INFINITY;

  Object.values(data).forEach((timeline) => {
    timeline.events.forEach((event) => {
      [event.intendedDay, event.completedDay].forEach((epochDay) => {
        if (epochDay === null) return;
        minEpochDay = Math.min(minEpochDay, epochDay);
        maxEpochDay = Math.max(maxEpochDay, epochDay);
      });
    });
  });

  if (!Number.isFinite(minEpochDay) || !Number.isFinite(maxEpochDay)) return null;
  return { startEpochDay: minEpochDay, endEpochDay: maxEpochDay };
};
